import { IonBackButton, IonButtons, IonFooter, IonHeader, IonPage } from "@ionic/react";
import { useEffect } from "react";
import { usePostHog } from "posthog-js/react";
import { useStore } from "zustand";
import { ChatContent } from "@/components/Chat/Content";
import { ChatFooter } from "@/components/Chat/Footer";
import { ChatInput } from "@/components/Chat/Input";
import { ChatMessage } from "@/components/Chat/Message";
import { ChatStoreContext, useChatStore } from "@/components/Chat/store";
import { Content } from "@/components/Global/Content";
import { Toolbar } from "@/components/Global/Toolbar";
import { ReflectionCard } from "@/components/Reflection/Card";
import { MultipleChoiceAnswer } from "@/components/Reflection/MultipleChoiceAnswer";
import { backendService } from "@/lib/backend";
import { type ReflectionTextResponseDto } from "@/lib/backend/api";
import { useExtractedMutation, useExtractedQuery } from "@/lib/backend/utils/hooks";
import { arrowLeftIcon } from "@/lib/icons/@heroicons/react/20/solid";
import { queryClient } from "@/lib/query";
import { useMutationStream } from "@/lib/query/hooks/useMutationStream";
import { Routes } from "@/lib/router";
import { useSelectorModalStore } from "@/lib/store/modal";

export function ReflectionQuestion() {
	const posthog = usePostHog();
	const presentModal = useSelectorModalStore.use.present();

	const chatStore = useChatStore();
	const chat = useStore(chatStore);

	const params = Routes.ReflectionQuestion.useParams();
	const questionId = params.id ?? "";

	const queryReflectionChat = useExtractedQuery({
		queryKey: ["reflection", questionId] as const,
		queryFn: (context) =>
			backendService.reflection.reflectionControllerGetReflectionById(context.queryKey[1], {
				signal: context.signal,
			}),
		enabled: !!questionId,
	});

	const reflectionChat = queryReflectionChat.data;
	const chatId = reflectionChat?.id ?? "";

	const [streamedResponse, mutationStream] = useMutationStream({
		mutationKey: ["reflection", "generate"],
		mutationFn: (params) =>
			backendService.reflection.reflectionControllerGenerateReflection(chatId, params),
		onSettled: async () => {
			await queryReflectionChat.refetch();
			chat.setResponding(false);
		},
	});

	const generateCoreValuesMutation = useExtractedMutation({
		mutationKey: ["reflection", "generateCoreValues"],
		mutationFn: (chatId: string) =>
			backendService.reflection.reflectionControllerGenerateCoreValues(chatId),
		onSuccess: (data) => {
			queryClient.invalidateQueries({ queryKey: ["reflection"] });
			queryClient.invalidateQueries({ queryKey: ["user", "me", "progression"] });
			presentModal("reflectionQuestionCompleted", {
				newSelectedCoreValues: data.create,
				updatedSelectedCoreValues: data.update,
			});
		},
	});

	const answerQuestionMutation = useExtractedMutation({
		mutationKey: ["reflection", "answerQuestion"],
		mutationFn: (variables: { chatId: string; data: ReflectionTextResponseDto }) =>
			backendService.reflection.reflectionControllerAnswerQuestion(variables.chatId, {
				content: variables.data.content,
			}),
		onSuccess: () => {
			mutationStream.mutate();
		},
	});

	useEffect(() => {
		if (
			reflectionChat?.messages.length === 0 &&
			reflectionChat?.status === "IN_PROGRESS" &&
			!mutationStream.isPending &&
			!chat.isResponding
		) {
			mutationStream.mutate();
			chat.setResponding(true);
		}

		if (reflectionChat?.status === "CHAT_COMPLETED") {
			generateCoreValuesMutation.mutate(chatId);
		}
	}, [reflectionChat?.status]);

	useEffect(() => {
		if (streamedResponse !== chat.streamAssistantMessage) {
			chat.setStreamAssistantMessage(streamedResponse);
		}
	}, [streamedResponse]);

	useEffect(() => {
		if (reflectionChat?.status === "SKIPPED") {
			presentModal("reflectionQuestionFirstAnswer", { id: questionId });
		}

		if (reflectionChat?.status === "FINISHED") {
			chat.setResponding(false);
			chat.setStreamAssistantMessage("");
			chat.setStreamUserMessage("");
		}

		if (reflectionChat?.status === "NOT_STARTED") {
			presentModal("reflectionQuestionFirstAnswer", { id: questionId });
			posthog.capture("Reflection Question First Answer", { questionId });
		}
	}, [reflectionChat?.status, questionId]);

	return (
		<ChatStoreContext.Provider value={chatStore}>
			<IonPage>
				<IonHeader className="ion-no-border">
					<Toolbar className="ion-p-2 plt-desktop:sm:ion-px-[max(calc(calc(100vw-640px)/2),--spacing(4))]">
						<IonButtons slot="secondary" className="flex w-full justify-between">
							<IonBackButton
								data-attr="page.reflection.question.back"
								data-touch-target
								defaultHref={Routes.Dashboard()}
								color="light"
								icon={arrowLeftIcon}
								text=""
								className="part-icon:m-0 part-icon:size-5 part-native:size-10 part-native:min-h-0 part-native:rounded-full part-native:bg-brown-300 part-native:p-1 part-native:text-xs part-native:text-brown-600 min-h-0"
							/>
						</IonButtons>
					</Toolbar>
				</IonHeader>

				<Content className="part-scroll:gap-8">
					<ChatContent>
						<ReflectionCard className="shrink-0 gap-2">
							<strong className="pt-2 text-base font-semibold text-pretty">
								{reflectionChat?.reflection?.initialMessageContent}
							</strong>
							{reflectionChat?.reflection?.initialMessageChoices?.length &&
							reflectionChat?.reflection?.initialMessageChoices?.length > 0 ? (
								<MultipleChoiceAnswer
									data-mask-text
									selected={reflectionChat.initialMessageChosen ?? []}
								/>
							) : (
								<p data-mask-text className="text-base font-normal">
									{reflectionChat?.initialMessageAnswer}
								</p>
							)}
						</ReflectionCard>
						{reflectionChat?.messages
							.filter((message) => message.content)
							.map((message) => (
								<ChatMessage key={message.id} sender={message.role === "USER" ? "user" : "bot"}>
									{message.content}
								</ChatMessage>
							))}
					</ChatContent>
				</Content>
				<IonFooter>
					<ChatFooter>
						{(reflectionChat?.status === "IN_PROGRESS" || generateCoreValuesMutation.isPending) && (
							<ChatInput
								focusOnLoad={reflectionChat?.status === "IN_PROGRESS"}
								canSendMessage={!generateCoreValuesMutation.isPending}
								onSubmit={async (content) => {
									posthog.capture("Reflection Question Answered", { questionId });
									answerQuestionMutation.mutate({ chatId, data: { content } });
									posthog.capture("Reflection Question Completed", { questionId });
									return true;
								}}
							/>
						)}
					</ChatFooter>
				</IonFooter>
			</IonPage>
		</ChatStoreContext.Provider>
	);
}
